import React from "react";
import { useSelector } from "react-redux";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  Divider,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import DownloadIcon from "@mui/icons-material/Download";
import DriveFileRenameOutlineIcon from "@mui/icons-material/DriveFileRenameOutline";
import InvestorSignatureModal from "./InvestorSignatureModal";
import {
  useConfirmLoanSignMutation,
  useInitiateLoanSignMutation,
} from "../../redux/api/authApi";

/**
 * Documents the borrower has to sign. The wizard counts steps from this list,
 * so adding an entry here turns on the Previous / Next Document navigation.
 */
const DOCUMENTS = [
  { key: "loan-agreement", title: "Loan Agreement", subtitle: "Loan Contract" },
];

const TERMS = [
  "1. The Borrower agrees to repay the loan amount with interest as per the schedule.",
  "2. The collateral asset will be held as security until full repayment.",
  "3. Late payments may incur additional fees as per platform policy.",
  "4. The Borrower has the right to prepay without penalty.",
];

const formatAmount = (amount, currency = "SAR") => {
  const num = Number(amount);
  if (!Number.isFinite(num)) return "—";
  return `${currency} ${num.toLocaleString("en-US", { maximumFractionDigits: 2 })}`;
};

/** `Borrower: Ahmed Al-Mansouri` — bold label, regular value. */
const TermLine = ({ label, value }) => (
  <Typography sx={{ fontSize: 16, lineHeight: "24px", color: "text.strong" }}>
    <Box component="span" sx={{ fontWeight: 700 }}>{`${label} `}</Box>
    {value}
  </Typography>
);

const InvestorSignDocumentsModal = ({ open, loan, onClose, onSigned }) => {
  const user = useSelector((state) => state.auth?.user);
  const [docIndex, setDocIndex] = React.useState(0);
  const [agreed, setAgreed] = React.useState({});
  const [signatureOpen, setSignatureOpen] = React.useState(false);
  const [envelopeId, setEnvelopeId] = React.useState(null);
  const [signError, setSignError] = React.useState("");

  const [initiateSign, { isLoading: initiating }] = useInitiateLoanSignMutation();
  const [confirmSign, { isLoading: confirming }] = useConfirmLoanSignMutation();

  React.useEffect(() => {
    if (!open) return;
    setDocIndex(0);
    setAgreed({});
    setSignatureOpen(false);
    setEnvelopeId(null);
    setSignError("");
  }, [open, loan?._id]);

  const total = DOCUMENTS.length;
  const doc = DOCUMENTS[docIndex] || DOCUMENTS[0];
  const isMultiStep = total > 1;
  const hasAgreed = Boolean(agreed[doc.key]);

  const borrower =
    [user?.firstName, user?.lastName].filter(Boolean).join(" ").trim() ||
    user?.name ||
    (typeof user?.email === "string" ? user.email.split("@")[0] : "") ||
    "—";

  /** Start the signing session, then collect the drawn signature. */
  const handleSign = async () => {
    if (!hasAgreed) return;
    setSignError("");
    try {
      const result = await initiateSign(loan?._id).unwrap();
      setEnvelopeId(result?.data?.envelopeId ?? null);
      setSignatureOpen(true);
    } catch (err) {
      setSignError(
        err?.data?.message || "Could not start the signing session. Please try again.",
      );
    }
  };

  const handleSignatureSubmit = async (signatureData) => {
    setSignError("");
    try {
      await confirmSign({ loanId: loan?._id, envelopeId, signatureData }).unwrap();
      setSignatureOpen(false);
      onSigned?.(loan);
      onClose?.();
    } catch (err) {
      setSignError(
        err?.data?.message || "Could not submit your signature. Please try again.",
      );
    }
  };

  const sectionPx = { px: 3 };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 1.5,
          border: (theme) => `1px solid ${theme.palette.border.light}`,
          overflow: "hidden",
        },
      }}
    >
      {/* Header: title, DocuSign mark, step progress */}
      <Box
        sx={{
          ...sectionPx,
          pt: 3,
          pb: 2.125,
          position: "relative",
          borderBottom: (theme) => `1px solid ${theme.palette.border.light}`,
        }}
      >
        <IconButton
          onClick={onClose}
          aria-label="Close"
          sx={{ position: "absolute", top: 12, right: 12, color: "text.muted", opacity: 0.7 }}
        >
          <CloseIcon sx={{ fontSize: 16 }} />
        </IconButton>

        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
          <Stack direction="row" spacing={1} alignItems="center">
            <DriveFileRenameOutlineIcon
              sx={{ fontSize: 20, color: (theme) => theme.palette.accent.blue.solid }}
            />
            <Typography sx={{ fontSize: 18, fontWeight: 700, color: "text.strong" }}>
              Sign Loan Documents
            </Typography>
          </Stack>
        </Stack>

        <Typography sx={{ fontSize: 14, color: "text.secondary", mt: 1 }}>
          {`Review and sign all required documents. Step ${docIndex + 1} of ${total}`}
        </Typography>

        <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
          {DOCUMENTS.map((d, i) => (
            <Box
              key={d.key}
              sx={{
                flex: 1,
                height: 6,
                borderRadius: 999,
                bgcolor: (theme) =>
                  i <= docIndex
                    ? theme.palette.accent.blue.solid
                    : theme.palette.background.tabTrack,
              }}
            />
          ))}
        </Stack>
      </Box>

      {/* Current document */}
      <Box
        sx={{
          ...sectionPx,
          py: 2,
          borderBottom: (theme) => `1px solid ${theme.palette.border.light}`,
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <DescriptionOutlinedIcon
              sx={{ fontSize: 20, color: (theme) => theme.palette.accent.blue.solid }}
            />
            <Box>
              <Typography sx={{ fontSize: 16, fontWeight: 600, color: "text.strong" }}>
                {doc.title}
              </Typography>
              <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                {doc.subtitle}
              </Typography>
            </Box>
          </Stack>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon sx={{ fontSize: 16 }} />}
            sx={{
              height: 32,
              px: 1.375,
              borderRadius: 1.25,
              fontSize: 14,
              fontWeight: 600,
              textTransform: "none",
              flexShrink: 0,
              borderColor: "border.light",
              color: "text.strong",
              bgcolor: "background.paper",
              "&:hover": { borderColor: "border.medium", bgcolor: "background.subtle" },
            }}
          >
            Download
          </Button>
        </Stack>
      </Box>

      {/* Document body */}
      <Box sx={{ ...sectionPx, py: 3, overflowY: "auto", flex: 1 }}>
        <Typography sx={{ fontSize: 16, lineHeight: "24px", color: "text.strong" }}>
          {`Loan Agreement - ${loan?.loanId || "—"}`}
        </Typography>
        <TermLine label="Borrower:" value={borrower} />
        <TermLine
          label="Loan Amount:"
          value={formatAmount(loan?.amount, loan?.currency)}
        />
        <TermLine
          label="Interest Rate:"
          value={
            Number.isFinite(Number(loan?.interestRate))
              ? `${loan.interestRate}% per annum`
              : "—"
          }
        />
        <TermLine
          label="Loan Term:"
          value={
            Number.isFinite(Number(loan?.termMonths)) ? `${loan.termMonths} months` : "—"
          }
        />
        <TermLine
          label="Monthly Payment:"
          value={formatAmount(loan?.monthlyPayment, loan?.currency)}
        />

        <Divider sx={{ my: 1.5 }} />

        <Typography sx={{ fontSize: 16, lineHeight: "24px", color: "text.strong" }}>
          Terms and Conditions
        </Typography>
        {TERMS.map((t) => (
          <Typography
            key={t}
            sx={{ fontSize: 16, lineHeight: "24px", color: "text.strong" }}
          >
            {t}
          </Typography>
        ))}
      </Box>

      {/* Acknowledgement */}
      <Box
        sx={{
          ...sectionPx,
          py: 2,
          bgcolor: "background.subtle",
          borderTop: (theme) => `1px solid ${theme.palette.border.light}`,
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
          <Checkbox
            checked={hasAgreed}
            onChange={(e) =>
              setAgreed((prev) => ({ ...prev, [doc.key]: e.target.checked }))
            }
            inputProps={{ "aria-label": "I have read and agree to the terms" }}
            sx={{
              p: 0,
              mt: 0.25,
              color: (theme) => theme.palette.border.medium,
              "&.Mui-checked": { color: (theme) => theme.palette.accent.blue.solid },
            }}
          />
          <Box>
            <Typography sx={{ fontSize: 14, fontWeight: 600, color: "text.strong" }}>
              I have read and agree to the terms of this document
            </Typography>
            <Typography sx={{ fontSize: 12, color: "text.secondary", mt: 0.5 }}>
              By checking this box, you acknowledge that you have reviewed and understood
              the content.
            </Typography>
          </Box>
        </Stack>
      </Box>

      {/* Actions */}
      <Box
        sx={{
          ...sectionPx,
          py: 2,
          bgcolor: "background.paper",
          borderTop: (theme) => `1px solid ${theme.palette.border.light}`,
        }}
      >
        {/* Initiate failures surface here; signature failures show in that dialog. */}
        {signError && !signatureOpen ? (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSignError("")}>
            {signError}
          </Alert>
        ) : null}

        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
          {/* Document navigation only makes sense with more than one document. */}
          {isMultiStep ? (
            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                disabled={docIndex === 0}
                onClick={() => setDocIndex((i) => Math.max(0, i - 1))}
                sx={{
                  height: 36,
                  px: 2.125,
                  borderRadius: 1.25,
                  fontSize: 14,
                  fontWeight: 600,
                  textTransform: "none",
                  borderColor: "border.light",
                  color: "text.strong",
                }}
              >
                Previous
              </Button>
              <Button
                variant="contained"
                disabled={docIndex >= total - 1 || !hasAgreed}
                onClick={() => setDocIndex((i) => Math.min(total - 1, i + 1))}
                sx={{
                  height: 36,
                  px: 2,
                  borderRadius: 1.25,
                  fontSize: 14,
                  fontWeight: 600,
                  textTransform: "none",
                  color: "common.white",
                  bgcolor: (theme) => theme.palette.accent.blue.solid,
                  boxShadow: "none",
                  "&:hover": {
                    bgcolor: (theme) => theme.palette.accent.blue.actionHover,
                    boxShadow: "none",
                  },
                }}
              >
                Next Document
              </Button>
            </Stack>
          ) : (
            <Box />
          )}

          <Stack direction="row" spacing={1}>
            <Button
              variant="contained"
              disabled={!hasAgreed || initiating}
              onClick={handleSign}
              startIcon={
                initiating ? (
                  <CircularProgress size={14} sx={{ color: "common.white" }} />
                ) : (
                  <DriveFileRenameOutlineIcon sx={{ fontSize: 16 }} />
                )
              }
              sx={{
                height: 36,
                px: 1.5,
                borderRadius: 1.25,
                fontSize: 14,
                fontWeight: 600,
                textTransform: "none",
                color: "common.white",
                bgcolor: (theme) => theme.palette.accent.blue.solid,
                boxShadow: "none",
                "&:hover": {
                  bgcolor: (theme) => theme.palette.accent.blue.actionHover,
                  boxShadow: "none",
                },
                "&.Mui-disabled": {
                  opacity: 0.5,
                  color: (theme) => theme.palette.common.white,
                  bgcolor: (theme) => theme.palette.accent.blue.solid,
                },
              }}
            >
              Sign Document
            </Button>
            <Button
              onClick={onClose}
              sx={{
                height: 36,
                px: 2,
                borderRadius: 1.25,
                fontSize: 14,
                fontWeight: 600,
                textTransform: "none",
                color: "text.strong",
              }}
            >
              Cancel
            </Button>
          </Stack>
        </Stack>
      </Box>

      <InvestorSignatureModal
        open={signatureOpen}
        onClose={() => setSignatureOpen(false)}
        onSubmit={handleSignatureSubmit}
        submitting={confirming}
        error={signatureOpen ? signError : ""}
      />
    </Dialog>
  );
};

export default InvestorSignDocumentsModal;
